"use client";

import React from "react";
import { ArrowUpRight, Loader, Mail, MapPin, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
declare global {
  interface Window {
    nomocoCf7?: {
      contactFormHtml?: string;
    };
    wpcf7?: {
      init?: (form: HTMLFormElement) => void;
      initForm?: (form: HTMLFormElement) => void;
    };
  }
}
function ContactForm7Embed() {
  const formWrapRef = React.useRef<HTMLDivElement | null>(null);
  const [formHtml, setFormHtml] = React.useState("");
  React.useEffect(() => {
    // already there on mount?
    if (window.nomocoCf7?.contactFormHtml) {
      setFormHtml(window.nomocoCf7.contactFormHtml);
      return;
    }

    // otherwise poll until the script populates it (or give up after ~5s)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.nomocoCf7?.contactFormHtml) {
        setFormHtml(window.nomocoCf7.contactFormHtml);
        clearInterval(interval);
      } else if (attempts > 50) {
        clearInterval(interval); // ~5s of polling, then stop trying
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);
  React.useEffect(() => {
    const wrapper = formWrapRef.current;
    if (!wrapper || !formHtml) return;
    const forms = wrapper.querySelectorAll<HTMLFormElement>("form.wpcf7-form");
    forms.forEach(form => {
      window.wpcf7?.init?.(form);
      window.wpcf7?.initForm?.(form);
    });
  }, [formHtml]);
  if (!formHtml) return null;
  return <div ref={formWrapRef} dangerouslySetInnerHTML={{
    __html: formHtml
  }} />;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactInfo = [{
    label: "info@NoMoCoPillow.com",
    icon: Mail,
    to: "#"
  }, {
    label: "+1 858-945-4496",
    icon: Phone,
    to: "#"
  }, {
    label: "NoMoCo Inc.",
    icon: MapPin,
    to: "#"
  }];
  return <section data-section-id="2897" id="get-in-touch" className="bg-background text-foreground overflow-hidden relative ![padding:80px_24px_0px_24px]">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
          <div className="lg:col-span-5 relative">
            <div className="hidden lg:block absolute -left-16 top-0 origin-top-left -rotate-90 translate-y-32">
              <span className="font-default uppercase text-xs tracking-[0.3em] text-accent whitespace-nowrap">
                CONTACT / 01
              </span>
            </div>

            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <span className="inline-block w-2 h-2 bg-primary" />
                <span className="font-default uppercase text-xs tracking-[0.25em] text-primary">
                  CLINICAL SUPPORT TEAM
                </span>
              </div>

              <h1 className="font-bold uppercase tracking-tighter text-primary !text-3xl">
                GET IN TOUCH                
              </h1>

              <p className="font-default text-lg md:text-xl leading-relaxed text-foreground/80 max-w-md">
                Our clinical support team is available to assist university imaging centers, hospitals, and clinics with product inquiries, technical specifications, and research support.
              </p>

              <div className="space-y-3 pt-4 border-t-[1.5px] border-accent/20">
                {contactInfo.map((item, i) => {
                const Icon = item.icon;
                return <Link key={i} data-index={i} to={item.to} className="group flex items-center gap-3 py-2 text-foreground hover:text-primary transition-colors">
                      <ArrowUpRight className="size-4 text-primary" />
                      <span className="font-default uppercase text-sm tracking-wider group-hover:underline underline-offset-4">
                        {item.label}
                      </span>
                    </Link>;
              })}
              </div>

              <div className="pt-6">
                <p className="font-default uppercase text-xs tracking-widest text-accent mb-2">
                  OFFICE HOURS
                </p>
                <p className="font-default text-lg text-foreground/80">Monday – Friday, 8:00 AM – 5:00 PM PST</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm7Embed />
          </div>
        </div>

        
      </div>
    </section>;
}